import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { bundleHasDiscount } from '../../util/bundlePricing';
import { buildCartOrderDataForListing } from '../../util/cartOrderDataFromListing';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { addToCart, addCartPromoCode, addBundleCartPromoCode } from '../../ducks/cart.duck';
import { loadData } from './BundlesPage.duck';

import { Page, IconArrowHead, IconSpinner, CartSidePanel } from '../../components';
import { BundleListingCard, BundleDetailModal } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './BundlesPage.module.css';

const BUNDLES_PER_PAGE = 12;

const BUNDLES_FAQ = [
  {
    question: 'Pourquoi des packs de produits domotique ?',
    answer:
      'La domotique fonctionne par écosystèmes : un capteur de mouvement prend tout son sens couplé à une ampoule connectée, une caméra s\'intègre mieux avec un NVR dédié, et un assistant vocal gagne en puissance avec des prises intelligentes compatibles. Nos packs reflètent cette logique : qu\'il s\'agisse de démarrer sa maison connectée, d\'étendre un système existant ou de sécuriser une pièce supplémentaire, chaque combinaison est pensée pour être immédiatement opérationnelle.',
  },
  {
    question: 'Comment sont conçus nos packs ?',
    answer:
      'Chaque pack est le fruit de notre expertise en domotique et maison connectée. Nous analysons les cas d\'usage réels de nos clients (sécurité, confort, énergie, automatisation), et sélectionnons des produits qui se complètent techniquement et fonctionnellement. Nous veillons à la compatibilité entre les appareils, à la cohérence des protocoles (Zigbee, Z-Wave, Wi-Fi, Matter…) et à la facilité d\'installation, pour vous proposer des ensembles prêts à l\'emploi sans mauvaise surprise.',
  },
  {
    question: 'Les produits d\'un pack sont-ils expédiés ensemble ?',
    answer:
      'L\'expédition dépend des vendeurs de chaque produit. Si les articles proviennent du même vendeur, ils peuvent être regroupés dans un seul colis. Dans le cas contraire, vous recevrez plusieurs livraisons distinctes.',
  },
];

const BundlesPage = props => {
  const {
    bundles,
    productListingsById,
    fetchInProgress,
    fetchError,
    scrollingDisabled,
    onManageDisableScrolling,
    onAddToCart,
    onAddCartPromoCode,
    onAddBundleCartPromoCode,
    dealsOnly = false,
  } = props;

  const intl = useIntl();
  const config = useConfiguration();
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [addingBundleId, setAddingBundleId] = useState(null);
  const [cartPanelOpen, setCartPanelOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [dealsFilter, setDealsFilter] = useState(dealsOnly);
  const [isAsideCollapsed, setIsAsideCollapsed] = useState(false);

  // Track whether the modal was ever opened so we don't wipe the URL on mount
  const hasBeenOpenRef = useRef(false);

  // Sync ?pack=<id> into the URL using the native history API so React Router
  // is not notified — avoids triggering loadData / re-renders / API re-fetches.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (selectedBundle) {
      hasBeenOpenRef.current = true;
      const id = String(selectedBundle.id);
      if (params.get('pack') !== id) {
        params.set('pack', id);
        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
      }
    } else if (hasBeenOpenRef.current) {
      // Modal was explicitly closed — clean the URL
      if (params.has('pack')) {
        params.delete('pack');
        const newSearch = params.toString();
        window.history.replaceState(
          null,
          '',
          newSearch ? `${window.location.pathname}?${newSearch}` : window.location.pathname
        );
      }
    }
    // If hasBeenOpenRef is false and selectedBundle is null (initial mount),
    // leave the URL untouched so the auto-open effect can read it.
  }, [selectedBundle]); // eslint-disable-line

  // Auto-open bundle from URL once bundles have loaded
  useEffect(() => {
    if (bundles.length === 0) return;
    // Read the live URL at this moment (not a stale closure value)
    const params = new URLSearchParams(window.location.search);
    const bundleId = params.get('pack');
    if (!bundleId || selectedBundle) return;
    const bundle = bundles.find(b => String(b.id) === bundleId);
    if (bundle) setSelectedBundle(bundle);
  }, [bundles.length]); // eslint-disable-line

  const filteredBundles = useMemo(() => {
    if (!dealsFilter) return bundles;
    return bundles.filter(b => bundleHasDiscount(b));
  }, [bundles, dealsFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredBundles.length / BUNDLES_PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * BUNDLES_PER_PAGE;
  const pageBundles = filteredBundles.slice(pageStart, pageStart + BUNDLES_PER_PAGE);

  const handleAddBundleToCart = useCallback(
    async bundle => {
      if (!bundle || !Array.isArray(bundle.products) || bundle.products.length === 0) return;
      setAddingBundleId(bundle.id);
      try {
        for (const product of bundle.products) {
          const productListing = productListingsById[product.id];
          if (!productListing) continue;
          const qty = Number(product.quantity) > 0 ? Number(product.quantity) : 1;
          const orderData = buildCartOrderDataForListing(productListing, qty);
          try {
            // eslint-disable-next-line no-await-in-loop
            await onAddToCart(productListing, orderData);
          } catch (itemError) {
            // One product failing should not stop the rest of the bundle.
          }
        }
        const bundlePromoCode = bundle?.price?.promo_code;
        if (bundlePromoCode && onAddCartPromoCode) {
          await onAddCartPromoCode(bundlePromoCode);
          await onAddBundleCartPromoCode(bundlePromoCode);
        }
      } catch (e) {
        // Surface via toast/analytics later.
      } finally {
        setAddingBundleId(null);
        setSelectedBundle(null);
        setCartPanelOpen(true);
      }
    },
    [productListingsById, onAddToCart, onAddCartPromoCode, onAddBundleCartPromoCode]
  );

  const pageTitle = dealsOnly
    ? intl.formatMessage({
        id: 'BundlesPage.promotionsTitle',
        defaultMessage: 'Promotions — Packs en réduction',
      })
    : intl.formatMessage({
        id: 'BundlesPage.bundlesTitle',
        defaultMessage: 'Packs domotique et maison connectée',
      });

  const pageDescription = dealsOnly
    ? intl.formatMessage({
        id: 'BundlesPage.promotionsDescription',
        defaultMessage:
          'La domotique fonctionne par écosystèmes — les bonnes combinaisons font toute la différence. Nos packs en promotion rassemblent des produits complémentaires et compatibles, sélectionnés par nos experts, à des tarifs réduits.',
      })
    : intl.formatMessage({
        id: 'BundlesPage.bundlesDescription',
        defaultMessage:
          'La domotique s\'installe rarement produit par produit. Nos packs regroupent des appareils complémentaires et techniquement compatibles, conçus à partir de cas d\'usage réels (sécurité, confort, énergie, automatisation), pour équiper ou étendre votre maison connectée en une seule fois.',
      });

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className={css.pagination}>
        <button
          type="button"
          className={css.pageButton}
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={safePage === 1}
          aria-label={intl.formatMessage({ id: 'BundlesPage.prevPage', defaultMessage: 'Page précédente' })}
        >
          <IconArrowHead direction="left" size="small" />
        </button>
        <span className={css.pageInfo}>
          {intl.formatMessage(
            { id: 'BundlesPage.pageInfo', defaultMessage: 'Page {current} / {total}' },
            { current: safePage, total: totalPages }
          )}
        </span>
        <button
          type="button"
          className={css.pageButton}
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={safePage === totalPages}
          aria-label={intl.formatMessage({ id: 'BundlesPage.nextPage', defaultMessage: 'Page suivante' })}
        >
          <IconArrowHead direction="right" size="small" />
        </button>
      </div>
    );
  };

  return (
    <Page scrollingDisabled={scrollingDisabled} title={pageTitle} description={pageDescription}>
      <TopbarContainer />

      <div className={css.layoutWrapperContainer}>
        <aside
          className={classNames(css.layoutWrapperFilterColumn, {
            [css.layoutWrapperFilterColumnCollapsed]: isAsideCollapsed,
          })}
        >
          <div className={css.filterColumnContent}>
            {!isAsideCollapsed && (
              <>
                <h2 className={css.filtersHeading}>
                  {intl.formatMessage({ id: 'BundlesPage.filtersHeading', defaultMessage: 'Filtres' })}
                </h2>

                <div className={css.filterGroup}>
                  <label className={css.checkboxLabel}>
                    <input
                      type="checkbox"
                      className={css.checkbox}
                      checked={dealsFilter}
                      onChange={e => {
                        setDealsFilter(e.target.checked);
                        setPage(1);
                      }}
                    />
                    {intl.formatMessage({
                      id: 'BundlesPage.filterDeals',
                      defaultMessage: 'Avec réduction',
                    })}
                  </label>
                </div>
              </>
            )}
          </div>

          <button
            type="button"
            className={classNames(css.toggleAsideButton, {
              [css.toggleAsideButtonCollapsed]: isAsideCollapsed,
            })}
            onClick={() => setIsAsideCollapsed(v => !v)}
            aria-label={
              isAsideCollapsed
                ? intl.formatMessage({ id: 'BundlesPage.expandFilters', defaultMessage: 'Afficher les filtres' })
                : intl.formatMessage({ id: 'BundlesPage.collapseFilters', defaultMessage: 'Masquer les filtres' })
            }
            aria-expanded={!isAsideCollapsed}
          >
            <IconArrowHead direction={isAsideCollapsed ? 'right' : 'left'} size="small" />
          </button>
        </aside>

        <div
          className={classNames(css.layoutWrapperMain, {
            [css.layoutWrapperMainExpanded]: isAsideCollapsed,
          })}
          role="main"
        >
          <div className={css.searchResultContainer}>
            <div className={css.resultHeader}>
              <h1 className={css.pageHeading}>{pageTitle}</h1>
              <p className={css.resultCount}>
                {intl.formatMessage(
                  {
                    id: 'BundlesPage.resultCount',
                    defaultMessage:
                      '{count, plural, one {# pack} other {# packs}}',
                  },
                  { count: filteredBundles.length }
                )}
              </p>
            </div>

            <p className={css.pageIntro}>{pageDescription}</p>

            {fetchInProgress ? (
              <div className={css.spinnerWrapper}>
                <IconSpinner />
              </div>
            ) : fetchError ? (
              <div className={css.errorMessage}>
                {intl.formatMessage({
                  id: 'BundlesPage.fetchError',
                  defaultMessage: 'Une erreur est survenue lors du chargement des packs.',
                })}
              </div>
            ) : filteredBundles.length === 0 ? (
              <div className={css.emptyMessage}>
                {intl.formatMessage({
                  id: 'BundlesPage.noResults',
                  defaultMessage: 'Aucun pack disponible pour le moment.',
                })}
              </div>
            ) : (
              <>
                <div className={css.bundleGrid}>
                  {pageBundles.map(bundle => (
                    <BundleListingCard
                      key={bundle.id}
                      bundle={bundle}
                      productListingsById={productListingsById}
                      onOpen={setSelectedBundle}
                      config={config}
                      className={css.bundleCard}
                    />
                  ))}
                </div>
                {renderPagination()}
              </>
            )}

            {!fetchInProgress && !fetchError ? (
              <section className={css.faqSection}>
                <h2 className={css.faqHeading}>FAQ</h2>
                <div className={css.faqList}>
                  {BUNDLES_FAQ.map((item, index) => (
                    <div key={`faq-${index}`} className={css.faqItem}>
                      <h3 className={css.faqQuestion}>{item.question}</h3>
                      <p className={css.faqAnswer}>{item.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </div>
      </div>

      <FooterContainer />

      <BundleDetailModal
        bundle={selectedBundle}
        productListingsById={productListingsById}
        isOpen={!!selectedBundle}
        onClose={() => setSelectedBundle(null)}
        onAddBundleToCart={handleAddBundleToCart}
        isAdding={!!(selectedBundle && addingBundleId === selectedBundle.id)}
        config={config}
        scrollingDisabled={scrollingDisabled}
        onManageDisableScrolling={onManageDisableScrolling}
      />

      <CartSidePanel
        open={cartPanelOpen}
        onClose={() => setCartPanelOpen(false)}
      />
    </Page>
  );
};

const mapStateToProps = state => {
  const { bundles, bundleProductIds, fetchInProgress, fetchError } = state.BundlesPage;

  const bundleProductRefs = (bundleProductIds || []).map(id => ({ id, type: 'listing' }));
  const bundleProductListings = getMarketplaceEntities(state, bundleProductRefs);
  const productListingsById = bundleProductListings.reduce((acc, listing) => {
    const id = listing?.id?.uuid || listing?.id;
    if (id) acc[id] = listing;
    return acc;
  }, {});

  return {
    bundles,
    productListingsById,
    fetchInProgress,
    fetchError,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onAddToCart: (listing, orderData) => dispatch(addToCart({ listing, orderData })),
  onAddCartPromoCode: code => dispatch(addCartPromoCode(code)),
  onAddBundleCartPromoCode: code => dispatch(addBundleCartPromoCode(code)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(BundlesPage);
